import { Bot, MessageSquare } from "lucide-react";

import { cn } from "@/lib";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import type { CreditPack, CreditPackStatus, Metric } from "@/services/billing";

interface CreditPacksListProps {
  packs: CreditPack[];
}

function formatInt(value: number): string {
  return value.toLocaleString("pt-BR");
}

function formatBrl(value: number): string {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function metricIcon(metric: Metric) {
  return metric === "ai_conversations" ? Bot : MessageSquare;
}

function metricLabel(metric: Metric): string {
  return metric === "ai_conversations" ? "Conversas IA" : "Mensagens IA";
}

const STATUS_LABELS: Record<CreditPackStatus, string> = {
  pending: "Pendente",
  confirmed: "Confirmado",
  failed: "Falhou",
  refunded: "Reembolsado",
};

const STATUS_STYLES: Record<CreditPackStatus, string> = {
  pending: "bg-amber-50 text-amber-700",
  confirmed:
    "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  refunded: "bg-zinc-100 text-zinc-700",
};

function isExpired(pack: CreditPack): boolean {
  if (!pack.expires_at) return false;
  const exp = new Date(pack.expires_at);
  if (Number.isNaN(exp.getTime())) return false;
  return exp.getTime() < Date.now();
}

export function CreditPacksList({ packs }: CreditPacksListProps) {
  if (packs.length === 0) {
    return (
      <section
        data-testid="credit-packs-empty"
        className="rounded-2xl border border-dashed border-border bg-white p-8 text-center text-sm text-muted-foreground"
      >
        Você ainda não comprou créditos extras. Use o botão "Comprar mais" no
        card de uso quando precisar.
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-border bg-white p-2 shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Métrica</TableHead>
            <TableHead className="text-right">Quantidade</TableHead>
            <TableHead className="text-right">Restante</TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Expira</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {packs.map((pack) => {
            const Icon = metricIcon(pack.metric);
            const expired = isExpired(pack);
            const effectiveStatus: CreditPackStatus =
              expired && pack.status === "confirmed" ? "refunded" : pack.status;
            const statusLabel = expired
              ? "Expirado"
              : STATUS_LABELS[pack.status];
            return (
              <TableRow
                key={pack.id}
                data-pack-id={pack.id}
                data-status={effectiveStatus}
                className={expired ? "opacity-60" : ""}
              >
                <TableCell>
                  <span className="inline-flex items-center gap-2 text-sm">
                    <Icon className="h-4 w-4 text-teal-500" />
                    {metricLabel(pack.metric)}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatInt(pack.amount)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatInt(pack.amount_remaining)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatBrl(pack.price_brl)}
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                      STATUS_STYLES[effectiveStatus]
                    )}
                  >
                    {statusLabel}
                  </span>
                </TableCell>
                <TableCell className="text-right text-sm text-muted-foreground">
                  {formatDate(pack.expires_at)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </section>
  );
}
